import React from 'react';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import { AirplaneButtonDimension, AirplaneCacheDimensionOption, SetItemSelection } from 'types/AirplaneCache';
import { defaultIsInStock, defaultIsSelected, makeOptionGroupDataTestId } from 'utils/airplaneCache';
import { HYDRA_PRODUCT_SIZING_PREDICTION } from 'constants/hydraTests';
import { useInAssignment } from 'hooks/useHydra';
import { selectPredictedSize, selectProductAirplaneCache } from 'selectors/product';
import useMartyContext from 'hooks/useMartyContext';
import AirplaneButton from 'components/productdetail/stylepicker/AirplaneButton';

import css from 'styles/components/productdetail/stylepicker/airplaneFieldsetOptions.scss';

interface AirplaneFieldsetOptionsProps {
  displayedOptions: AirplaneCacheDimensionOption[];
  isInStockFn?: (option: AirplaneCacheDimensionOption) => boolean;
  isSelectedFn?: (option: AirplaneCacheDimensionOption) => boolean;
  radioButtonName: string;
  setItemSelection: SetItemSelection;
  typeKey: AirplaneButtonDimension;
  useNarrowOptionButtons?: boolean;
  namespace: string;
  isHydraCantFindYourSize?: boolean;
}

const AirplaneFieldsetOptions = ({
  displayedOptions,
  isInStockFn,
  isSelectedFn,
  radioButtonName,
  setItemSelection,
  typeKey,
  useNarrowOptionButtons,
  namespace,
  isHydraCantFindYourSize
}: AirplaneFieldsetOptionsProps) => {
  const { testId } = useMartyContext();

  const airplaneCache = useSelector(selectProductAirplaneCache);
  const predictedSize = useSelector(selectPredictedSize);
  const isHydraSizePrediction = useInAssignment(HYDRA_PRODUCT_SIZING_PREDICTION);

  if (!airplaneCache) {
    return null;
  }

  if (!(displayedOptions.length > 0)) {
    return <p className={css.noOptions}>No options available. Please modify the selection above.</p>;
  }

  const optionGroupTestId = testId(makeOptionGroupDataTestId(typeKey));
  const optionsClassName = cn(css.airplaneSizingContainer, {
    [css.narrowOptionButtons]: useNarrowOptionButtons,
    [css.isHydraCantFindYourSize]: isHydraCantFindYourSize
  });

  return (
    <div className={optionsClassName} data-test-id={optionGroupTestId}>
      {displayedOptions.map(option => {
        const isInStock = isInStockFn ? isInStockFn(option) : defaultIsInStock(typeKey, airplaneCache, option);
        const isSelected = isSelectedFn ? isSelectedFn(option) : defaultIsSelected(typeKey, airplaneCache, option);
        const isRecommended = isHydraSizePrediction && option.label === predictedSize;

        return (
          <AirplaneButton
            isInStock={isInStock}
            isSelected={isSelected}
            isRecommended={isRecommended}
            key={option.id}
            option={option}
            radioButtonName={radioButtonName}
            setItemSelection={setItemSelection}
            typeKey={typeKey}
            namespace={namespace}
          />
        );
      })}
    </div>
  );
};

export default AirplaneFieldsetOptions;
